import {
	type Dispatch,
	type MutableRefObject,
	type SetStateAction,
	useMemo,
	useRef,
	useState,
} from 'react';
import { debounce } from 'lodash';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { isNetworkOrClientErrorCode } from '@atlassian/help-center-common-util/error-codes';
import type { FieldChangedLifecycleEvent } from '@atlassian/proforma-form-renderer/lifecycle-handlers';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';
import { Action } from '@atlassian/proforma-utils/analytics/events';
import type { RequestCreateKBState as RequestCreateKnowledgeBaseState } from 'state/persisted/request-create';

import { useKnowledgeBase } from '../../../../controllers/request-create-context-provider';
import { useKnowledgeBaseSearchService } from '../../../../services';
import type {
	KnowledgeBaseSearchRequest,
	KnowledgeArticleResponse,
	KnowledgeBaseSearchResponse,
} from '../../../../services/knowledge-base-search/types';
import type { KnowledgeArticle } from './types';

export const mapArticle = (article: KnowledgeArticleResponse): KnowledgeArticle => ({
	id: article.articleId,
	name: article.articleTitle,
	articleSnippet: article.articleSnippet,
	pageVersion: article.articleVersion,
	score: article.snippetScore,
	isSmartSnippet: article.isSmartSnippet,
	linkedPortals: article.linkedPortals,
	sourceType: article.articleSource,
	articleUrl: article.articleUrl,
});

const USER_TYPING_DEBOUNCE_TIME = 500;

// These don't have matching entries in the ActionSubject / Action enums in
// @atlassian/proforma-utils/analytics — they're help-center-specific values carried
// over from the legacy use-on-field-change hook. Not worth adding to the platform
// package since these analytics will be replaced with UFO instrumentation.
const KB_SUGGESTION_ACTION_SUBJECT = 'kbSuggestionFieldProforma';
const KB_SUGGESTION_LOADED_ACTION = 'loaded';

export interface HandleFieldChangedParams {
	event: FieldChangedLifecycleEvent;
	knowledgeBase: RequestCreateKnowledgeBaseState | undefined;
	knowledgeBaseSearchFetch: (
		req: KnowledgeBaseSearchRequest,
	) => Promise<KnowledgeBaseSearchResponse>;
	searchCounterRef: MutableRefObject<number>;
	setKnowledgeBaseArticles: Dispatch<SetStateAction<KnowledgeArticle[]>>;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}

// `event.fieldId` is normalized to a string in ProformaRequestCreate's FIELD_CHANGED handler before this runs.
export const handleFieldChanged = ({
	event,
	knowledgeBase,
	knowledgeBaseSearchFetch,
	searchCounterRef,
	setKnowledgeBaseArticles,
	createAnalyticsEvent,
}: HandleFieldChangedParams) => {
	const fieldValue = event.fieldValue;
	const hasText = fieldValue && 'text' in fieldValue;
	const trimmedValue = hasText ? fieldValue.text.trim() : '';

	if (!trimmedValue) {
		setKnowledgeBaseArticles([]);
		return;
	}

	searchCounterRef.current += 1;
	const currentSearch = searchCounterRef.current;
	const isStaleSearch = () => searchCounterRef.current !== currentSearch;

	knowledgeBaseSearchFetch({
		labels: knowledgeBase ? knowledgeBase.labels : [],
		query: trimmedValue,
		resultLimit: 3,
	})
		.then((response) => {
			if (isStaleSearch()) {
				return;
			}
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${KB_SUGGESTION_ACTION_SUBJECT} ${KB_SUGGESTION_LOADED_ACTION}`,
			);
			setKnowledgeBaseArticles(response.articles.map(mapArticle));
		})
		.catch((error: unknown) => {
			const status =
				error instanceof Error && 'status' in error
					? (error as { status: number }).status
					: undefined;
			if (status === undefined || !isNetworkOrClientErrorCode(status)) {
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`${KB_SUGGESTION_ACTION_SUBJECT} ${Action.FAILED}`,
				);
			}
		});
};

export const useKnowledgeBaseSuggestions = (proformaForm: ProformaForm) => {
	di(useKnowledgeBase, useKnowledgeBaseSearchService, fireTrackAnalytics);

	const knowledgeBase = useKnowledgeBase();
	const { fetch: knowledgeBaseSearchFetch } = useKnowledgeBaseSearchService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [knowledgeBaseArticles, setKnowledgeBaseArticles] = useState<KnowledgeArticle[]>([]);
	const searchCounterRef = useRef(0);

	const summaryFieldId = useMemo(() => {
		for (const [fieldId, question] of Object.entries(proformaForm.design.questions)) {
			if (question.jiraField === 'summary') {
				return fieldId;
			}
		}
		return null;
	}, [proformaForm]);

	const onFieldChanged = useMemo(() => {
		const debouncedOnSummaryFieldChanged = debounce((event: FieldChangedLifecycleEvent) => {
			handleFieldChanged({
				event,
				knowledgeBase,
				knowledgeBaseSearchFetch,
				searchCounterRef,
				setKnowledgeBaseArticles,
				createAnalyticsEvent,
			});
		}, USER_TYPING_DEBOUNCE_TIME);

		return (event: FieldChangedLifecycleEvent) => {
			if (!summaryFieldId || event.fieldId !== summaryFieldId) {
				return;
			}
			debouncedOnSummaryFieldChanged(event);
		};
	}, [summaryFieldId, knowledgeBase, knowledgeBaseSearchFetch, createAnalyticsEvent]);

	return { summaryFieldId, knowledgeBaseArticles, onFieldChanged };
};
